import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'

import { Package } from 'lucide-react'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { Product } from '@/lib/api/product-api'
import { useInfiniteProducts } from '@/hooks/use-product-query'
import { formatQty, formatVND } from '@/lib/format'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

interface Props {
    workflow?: WorkflowSearchTracker
    searchTerm: string
    onSelect: (product: Product) => void
    /** Ô trống mà vẫn mở: duyệt toàn bộ SP đang bán. Cả duyệt lẫn tìm theo từ
     *  khóa đều cuộn tới cuối là tải thêm trang. */
    browse?: boolean
}

export interface PosProductSearchDropdownHandle {
    /** Number of visible results (post-debounce). */
    getCount: () => number
    /** Move highlighted row. Returns true if a row is now highlighted. */
    moveSelection: (delta: 1 | -1) => boolean
    /** Invoke onSelect for currently highlighted row. Returns true on success. */
    selectCurrent: () => boolean
}

const PAGE_SIZE = 20
const DEBOUNCE_MS = 300
// Cách đáy bao nhiêu px thì tải trang sau.
const LOAD_MORE_THRESHOLD_PX = 48

// Parent controls keyboard nav imperatively. Previous native
// addEventListener approach raced React synthetic handlers with USB
// scanner input, selecting the wrong product into the cart.
export const PosProductSearchDropdown = forwardRef<
    PosProductSearchDropdownHandle,
    Props
>(function PosProductSearchDropdown(
    { searchTerm, onSelect, workflow, browse = false },
    ref,
) {
    const [debouncedTerm, setDebouncedTerm] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const trimmed = searchTerm.trim()
        if (!trimmed) {
            setDebouncedTerm('')
            return
        }
        const timer = setTimeout(() => setDebouncedTerm(trimmed), DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchTerm])

    // Tìm theo từ khóa cũng tải dần theo trang (M-43): trước đây chỉ lấy 8 SP
    // đầu, từ khóa khớp nhiều SP thì thu ngân không thấy hết để chọn.
    // Chưa gõ gì thì không gọi /products.
    const searchQuery = useInfiniteProducts(
        { search: debouncedTerm, is_active: true, per_page: PAGE_SIZE },
        Boolean(debouncedTerm),
    )
    const {
        data: searchData,
        isFetching,
        isError,
        isPlaceholderData,
    } = searchQuery
    const searchResults = useMemo(
        () => searchData?.pages.flatMap((p) => p.data ?? []) ?? [],
        [searchData],
    )
    const browsing = browse && !searchTerm.trim()
    const browseQuery = useInfiniteProducts(
        { is_active: true, per_page: PAGE_SIZE },
        browsing,
    )
    const browseResults = useMemo(
        () => browseQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [browseQuery.data],
    )
    const active = browsing || Boolean(debouncedTerm)
    const results = browsing
        ? browseResults
        : debouncedTerm
          ? searchResults
          : []
    const searchId = useMemo(
        () => (workflow ? crypto.randomUUID() : undefined),
        [workflow, debouncedTerm],
    )
    const lastResult = useRef('')
    const settled = Boolean(
        debouncedTerm &&
        searchTerm.trim() === debouncedTerm &&
        searchData &&
        !isFetching &&
        !isError &&
        !isPlaceholderData,
    )
    const hasMore = Boolean(searchQuery.hasNextPage)
    useEffect(() => {
        if (!workflow || !searchId || !settled) return
        // Một từ khóa = một sự kiện: cuộn tải thêm trang không phát lại.
        const key = searchId
        if (lastResult.current === key) return
        lastResult.current = key
        workflow.search({
            searchId,
            entityKind: 'product',
            queryLength: debouncedTerm.length,
            resultCount: results.length,
            hasMore,
        })
    }, [workflow, searchId, settled, results.length, hasMore, debouncedTerm])
    // Duyệt (ô trống) cũng là một lượt tìm: đo "mở ra có chọn được không".
    const browseSearchId = useMemo(
        () => (workflow && browsing ? crypto.randomUUID() : undefined),
        [workflow, browsing],
    )
    const browseSettled = Boolean(
        browsing &&
        browseQuery.data &&
        !browseQuery.isFetching &&
        !browseQuery.isError,
    )
    const browseHasMore = Boolean(browseQuery.hasNextPage)
    useEffect(() => {
        if (!workflow || !browseSearchId || !browseSettled) return
        // Một lần mở danh sách = một sự kiện: cuộn tải thêm trang không phát lại,
        // nếu không query đếm "số lần mở" sẽ bị thổi phồng theo số trang.
        const key = browseSearchId
        if (lastResult.current === key) return
        lastResult.current = key
        workflow.search({
            searchId: browseSearchId,
            entityKind: 'product',
            queryKind: 'browse',
            queryLength: 0,
            resultCount: browseResults.length,
            hasMore: browseHasMore,
        })
    }, [
        workflow,
        browseSearchId,
        browseSettled,
        browseResults.length,
        browseHasMore,
    ])
    const select = (product: Product, index: number) => {
        const correlation = browsing
            ? browseSettled
                ? { searchId: browseSearchId, candidateRank: index + 1 }
                : {}
            : settled
              ? { searchId, candidateRank: index + 1 }
              : {}
        workflow?.setContext({ entryMethod: 'manual' })
        workflow?.step('product_selected', {
            entryMethod: 'manual',
            ...correlation,
        })
        onSelect(product)
    }

    // Reset/kẹp highlight ngay trong render, KHÔNG để trong effect: effect chạy
    // sau commit nên lệnh reset xếp hàng sau thao tác bàn phím cùng nhịp, và
    // danh sách mới kịp hiện ra với dòng highlight của lượt tìm trước — Enter
    // hay máy quét rơi đúng lúc đó là thêm nhầm SP vào giỏ.
    const selectionScope = `${browsing}:${debouncedTerm}`
    const [selectionScopeState, setSelectionScopeState] =
        useState(selectionScope)
    const maxIndex = Math.max(results.length - 1, 0)
    if (selectionScopeState !== selectionScope) {
        setSelectionScopeState(selectionScope)
        setSelectedIndex(0)
    } else if (selectedIndex > maxIndex) {
        setSelectedIndex(maxIndex)
    }

    useImperativeHandle(
        ref,
        () => ({
            getCount: () => (active ? results.length : 0),
            moveSelection: (delta) => {
                if (!active || results.length === 0) return false
                setSelectedIndex((i) =>
                    delta === 1
                        ? Math.min(i + 1, results.length - 1)
                        : Math.max(i - 1, 0),
                )
                return true
            },
            selectCurrent: () => {
                if (!active || results.length === 0) return false
                select(results[selectedIndex], selectedIndex)
                return true
            },
        }),
        [
            active,
            results,
            selectedIndex,
            onSelect,
            workflow,
            searchId,
            settled,
            browsing,
            browseSearchId,
            browseSettled,
        ],
    )

    useEffect(() => {
        const list = listRef.current
        if (!list) return
        const item = list.children[selectedIndex] as HTMLElement | undefined
        item?.scrollIntoView({ block: 'nearest' })
    }, [selectedIndex])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const query = browsing ? browseQuery : searchQuery
        const el = e.currentTarget
        const nearBottom =
            el.scrollTop + el.clientHeight >=
            el.scrollHeight - LOAD_MORE_THRESHOLD_PX
        if (nearBottom && query.hasNextPage && !query.isFetchingNextPage) {
            void query.fetchNextPage()
        }
    }

    if (!active || results.length === 0) return null

    return (
        <div
            ref={listRef}
            data-testid="pos-product-search-list"
            onScroll={handleScroll}
            className="absolute top-full left-0 mt-1 w-[30rem] max-w-[calc(100vw-1rem)] max-h-96 overflow-y-auto rounded-md bg-popover border shadow-lg z-50"
        >
            {results.map((product, i) => {
                const primaryImg =
                    product.images?.find((img) => img.is_primary) ??
                    product.images?.[0]
                // Tồn lấy thẳng từ bản ghi SP: `/products` đã trả tổng tồn mọi
                // lô active + phần đang nằm trên kệ bán POS, khỏi query riêng.
                const stock = product.stock_available
                const posStock = product.pos_stock_available ?? 0
                return (
                    <button
                        key={product.id}
                        type="button"
                        onMouseDown={(e) => {
                            // Prevent input blur before click registers
                            e.preventDefault()
                            select(product, i)
                        }}
                        onMouseEnter={() => setSelectedIndex(i)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                            i === selectedIndex
                                ? 'bg-accent'
                                : 'hover:bg-accent/50'
                        }`}
                    >
                        <div className="size-12 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                            {primaryImg ? (
                                <img
                                    src={getProductImageURL(
                                        primaryImg.image_url,
                                    )}
                                    alt={product.name}
                                    className="size-full object-cover"
                                    loading="lazy"
                                    // Ảnh lỗi/thiếu file thì ẩn hẳn, để lại ô
                                    // xám thay vì icon ảnh vỡ.
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none'
                                    }}
                                />
                            ) : (
                                <Package className="size-5 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">
                                {product.name}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
                                <span>{product.sku}</span>
                                {(product.weight_value ?? 0) > 0 && (
                                    <span className="px-1 py-px rounded bg-sky-100 text-sky-800 text-[10px] leading-none">
                                        {m.pos_product_net_weight({
                                            weight: `${formatQty(product.weight_value ?? 0)} ${product.weight_unit || 'kg'}`,
                                        })}
                                    </span>
                                )}
                                {typeof stock === 'number' && (
                                    <span
                                        className={
                                            stock > 0
                                                ? 'px-1 py-px rounded bg-emerald-100 text-emerald-700 text-[10px] leading-none'
                                                : 'px-1 py-px rounded bg-destructive/10 text-destructive text-[10px] leading-none'
                                        }
                                    >
                                        {m.order_product_stock_badge({
                                            qty: formatQty(stock),
                                            unit: product.unit ?? '',
                                        })}
                                    </span>
                                )}
                                {/* Kệ bán POS: chỉ hiện khi trên kệ CÒN hàng —
                                    chưa gắn kệ nào thì số luôn 0, hiện ra chỉ
                                    làm nhiễu dòng SP. */}
                                {posStock > 0 && (
                                    <span className="px-1 py-px rounded bg-amber-100 text-amber-800 text-[10px] leading-none">
                                        {m.pos_shelf_stock_badge({
                                            qty: formatQty(posStock),
                                            unit: product.unit ?? '',
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-sm font-medium text-primary shrink-0">
                            {formatVND(product.unit_price)}
                            {String.fromCharCode(273)}
                        </div>
                    </button>
                )
            })}
        </div>
    )
})
